import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { FsMapViewVector, SnMapView, FsBookmark } from '@lib/api/entities';
import { t } from '@lib/translation';
import { IConfirmDialogConfig, SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnMapViewsService {
  public tabPermissions: string[] = [];

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getMapViewList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnMapView>> {
    return this._httpClient.get('/api/map-views/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((mapView) => new SnMapView(mapView)),
      })),
    );
  }

  public getMapView(id: string): Observable<SnMapView> {
    return this._httpClient
      .get(`/api/map-views/${id}/`)
      .pipe(map((response: any) => new SnMapView(response)));
  }

  public createMapView(data: SnMapView, showToast: boolean = true): Observable<SnMapView> {
    return this._httpClient.post('/api/map-views/', data).pipe(
      map((response: any) => new SnMapView(response)),
      tap({
        next: () => {
          if (showToast) {
            this._toastrService.success('Map view created');
          }
        },
        error: () => {
          if (showToast) {
            this._toastrService.error('Could not create map view. Please try again later');
          }
        },
      }),
    );
  }

  public updateMapView(id: string, data: SnMapView | any, silent?: boolean): Observable<SnMapView> {
    const payload = {
      ...data,
      user: data.user?.id,
    };
    return this._httpClient.patch(`/api/map-views/${id}/`, payload).pipe(
      map((response: any) => new SnMapView(response)),
      tap({
        next: () => (!silent ? this._toastrService.success('Map view updated') : null),
        error: () => this._toastrService.error('Could not update map view. Please try again later'),
      }),
    );
  }

  public deleteMapView(
    view: SnMapView,
    options?: {
      message?: string;
      archiveButtonText?: string;
      deleteButtonText?: string;
      successMessage?: string;
      errorMessage?: string;
      disableAltBtn?: boolean;
    }
  ): Observable<any> {
    const {
      message = 'map.mapView.deleteConfirmation.message',
      archiveButtonText = 'common.archive',
      deleteButtonText = 'common.delete',
      disableAltBtn = false
    } = options || {};

    const config: IConfirmDialogConfig = {
      message: message.replace('{name}', view.name),
      confirmButton: {
        text: deleteButtonText,
        color: 'warn',
      },
    };

    if (!disableAltBtn) {
      config.alternativeButton = {
        text: archiveButtonText,
        color: 'accent',
        value: 'archive',
      };
    }

    return this._dialogService
      .confirm(config)
      .pipe(
        switchMap((res) => (res === true ? this.deleteMapViews(view) : this.archiveMapView(view))),
      );
  }

  public archiveMapView(view: SnMapView): Observable<Object> {
    return this.updateMapView(view.id, {
      ...view,
      archived: true,
    });
  }

  public deleteMapViews(view: SnMapView): Observable<Object> {
    return this._httpClient.delete(`/api/map-views/${view.id}/`).pipe(
      tap({
        next: () => this._toastrService.success(`${view.name} deleted`),
        error: () =>
          this._toastrService.error(
            `${view.name} cannot be deleted. You may not have proper permission`,
          ),
      }),
    );
  }

  public sendMapViewTo(
    id: string,
    data: { users?: string[]; teams?: string[] },
  ): Observable<SnMapView> {
    return this._httpClient.post(`/api/map-views/${id}/send/`, data).pipe(
      map((response: any) => new SnMapView(response)),
      tap({
        next: () => this._toastrService.success('The view has been sent successfully'),
        error: () =>
          this._toastrService.error(
            'Unable to send the view. Please fill out fields or try again later',
          ),
      }),
    );
  }

  public getVectorList(
    mapViewId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsMapViewVector>> {
    return this._httpClient.get(`/api/map-views/${mapViewId}/vectors/`, { params }).pipe(
      tap({
        error: () =>
          this._toastrService.error('Unable to retrieve vectors. Please try again later'),
      }),
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.features.map((vector) => new FsMapViewVector(vector)),
      })),
    );
  }

  public createVector(
    mapViewId: string,
    data: any,
    showToast: boolean = true,
  ): Observable<FsMapViewVector> {
    return this._httpClient.post(`/api/map-views/${mapViewId}/vectors/`, data).pipe(
      map((response: any) => new FsMapViewVector(response)),
      tap({
        next: () => {
          if (showToast) {
            this._toastrService.success('Shape added');
          }
        },
        error: () => {
          if (showToast) {
            this._toastrService.error('Could not add shape. Please try again later');
          }
        },
      }),
    );
  }

  public updateVector(mapViewId: string, vectorId: string, data: any): Observable<FsMapViewVector> {
    return this._httpClient.patch(`/api/map-views/${mapViewId}/vectors/${vectorId}/`, data).pipe(
      map((response: any) => new FsMapViewVector(response)),
      tap({
        next: () => this._toastrService.success('Shape updated'),
        error: () => this._toastrService.error('Could not update shape. Please try again later'),
      }),
    );
  }

  public deleteVector(mapViewId: string, vectorId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        title: t('common.confirm'),
        message: t('incidentManagement.dialogs.confirmations.deleteShape'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
        cancelButton: {
          text: t('common.cancel'),
          color: 'primary',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.delete(`/api/map-views/${mapViewId}/vectors/${vectorId}/`),
        ),
        tap({
          next: () => this._toastrService.success('Shape deleted'),
          error: () => this._toastrService.error('Unable to delete shape. Please try again later'),
        }),
      );
  }

  public getBookmarks(params?: ISnListQueryParameters): any {
    return this._httpClient.get('/api/maps/bookmarks/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((bookmark) => new FsBookmark(bookmark)),
      })),
    );
  }

  public addBookmarks(data: any): any {
    return this._httpClient.post('/api/maps/bookmarks/', data).pipe(
      tap({
        next: () => this._toastrService.success('Bookmark saved'),
        error: () => this._toastrService.error('Could not add bookmark. Please try again later'),
      }),
    );
  }

  public updateBookmark(data: any, bookmarkId: string): any {
    return this._httpClient.patch(`/api/maps/bookmarks/${bookmarkId}/`, data).pipe(
      map((response: any) => new FsBookmark(response)),
      tap({
        next: () => this._toastrService.success('Bookmark updated'),
        error: () => this._toastrService.error('Could not update bookmark. Please try again later'),
      }),
    );
  }

  public deleteBookmark(
    bookmark: FsBookmark,
    options: {
      title?: string;
      message?: string;
      confirmButtonText?: string;
      cancelButtonText?: string;
      successMessage?: string;
      errorMessage?: string;
    } = {}
  ): Observable<object> {
    const {
      title,
      message = 'map.bookmarks.deleteConfirmation.message',
      confirmButtonText = 'common.delete',
      cancelButtonText = 'common.cancel',
      successMessage = 'map.bookmarks.deleteConfirmation.successMessage',
      errorMessage = 'map.bookmarks.deleteConfirmation.errorMessage'
    } = options;

    const confirmOptions: any = {
      message,
      confirmButton: {
        text: confirmButtonText,
        color: 'warn',
      },
    };

    if (title) {
      confirmOptions.title = title;
    }

    if (cancelButtonText) {
      confirmOptions.cancelButton = {
        text: cancelButtonText,
      };
    }

    return this._dialogService
      .confirm({
        title: t('common.confirm'),
        message: t('incidentManagement.dialogs.confirmations.deleteBookmark'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
        cancelButton: {
          text: t('common.cancel'),
          color: 'primary',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/maps/bookmarks/${bookmark.id}/`)),
        tap({
          next: () => this._toastrService.success(successMessage),
          error: () => this._toastrService.error(errorMessage),
        }),
      );
  }
}
